import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import { ArrowLeft, CheckCircle2, XCircle, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const ChallengeInterface = ({ stage, challenges, onBack, onComplete, userProgress }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [score, setScore] = useState(0);
  const [showKnowledgeBaseAlert, setShowKnowledgeBaseAlert] = useState(false);

  const currentQuestion = challenges[currentQuestionIndex];
  const totalQuestions = challenges.length;
  const progressPercentage = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const handleAnswerSelect = (index) => {
    if (!showResult) {
      setSelectedAnswer(index);
    }
  };

  const handleSubmit = () => {
    if (selectedAnswer === null) {
      toast({
        title: "Select an answer",
        description: "Please choose an option before submitting.",
        variant: "destructive"
      });
      return;
    }

    const correct = selectedAnswer === currentQuestion.correctAnswer;
    setIsCorrect(correct);
    setShowResult(true);

    if (correct) {
      setScore(score + 1);
      toast({
        title: "Correct! ✓",
        description: "Well done! You're mastering the process.",
      });
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);

      if (newAttempts >= 4) {
        setShowKnowledgeBaseAlert(true);
        toast({
          title: "Study Time",
          description: "Visit the Knowledge Base to strengthen your understanding.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Incorrect",
          description: `Attempts: ${newAttempts}/4. Review the explanation and try again.`,
          variant: "destructive"
        });
      }
    }
  };

  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setIsCorrect(false);
    } else {
      // Challenge completed
      const finalScore = (score / totalQuestions) * 100;
      if (finalScore >= 60 && attempts < 4) {
        onComplete(stage.id, score, attempts);
      } else {
        toast({
          title: "Challenge Not Passed",
          description: "Review the material and try again.",
          variant: "destructive"
        });
        setTimeout(() => onBack(), 2000);
      }
    }
  };

  const handleRetry = () => {
    if (attempts >= 4) {
      setShowKnowledgeBaseAlert(true);
      return;
    }
    setSelectedAnswer(null);
    setShowResult(false);
    setIsCorrect(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={onBack}
            className="mb-4 hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Stage
          </Button>

          <Card className="border-2 border-purple-300 bg-gradient-to-r from-purple-50 to-blue-50">
            <CardHeader>
              <CardTitle className="text-2xl text-purple-900">{stage.title} - Challenge</CardTitle>
              <CardDescription className="text-slate-700 mt-1">
                Question {currentQuestionIndex + 1} of {totalQuestions}
              </CardDescription>
              <Progress value={progressPercentage} className="mt-3 h-2" />
            </CardHeader>
          </Card>
        </div>

        {/* Knowledge Base Alert */}
        {showKnowledgeBaseAlert && (
          <Alert className="mb-6 border-2 border-red-400 bg-gradient-to-r from-red-50 to-orange-50">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <AlertDescription className="ml-2">
              <p className="font-semibold text-slate-900 mb-2">
                You've reached the maximum attempts (4). Time to study!
              </p>
              <a
                href="https://knowledgebase.live"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-purple-700 hover:text-purple-900 font-semibold underline transition-colors"
              >
                Visit knowledgebase.live to learn more
                <ExternalLink className="w-4 h-4" />
              </a>
            </AlertDescription>
          </Alert>
        )}

        {/* Attempts Counter */}
        <div className="mb-6 text-center">
          <p className="text-slate-600">
            Attempts: <span className={`font-bold ${attempts >= 3 ? 'text-red-600' : 'text-purple-700'}`}>{attempts}/4</span>
            {' | '}
            Score: <span className="font-bold text-green-600">{score}/{totalQuestions}</span>
          </p>
        </div>

        {/* Question Card */}
        <Card className="mb-6 border-2 border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100">
            {currentQuestion.scenario && (
              <div className="mb-4 p-4 bg-blue-50 border-l-4 border-blue-700 rounded">
                <p className="text-sm font-semibold text-blue-900 mb-1">SCENARIO</p>
                <p className="text-slate-700 text-base leading-relaxed">
                  Real-world situation - use your knowledge to respond correctly.
                </p>
              </div>
            )}
            <CardTitle className="text-xl text-slate-900 leading-relaxed">
              {currentQuestion.question}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showResult}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                    selectedAnswer === index
                      ? showResult
                        ? isCorrect
                          ? 'border-green-500 bg-green-50'
                          : 'border-red-500 bg-red-50'
                        : 'border-purple-500 bg-purple-50'
                      : showResult && index === currentQuestion.correctAnswer
                      ? 'border-green-500 bg-green-50'
                      : 'border-slate-300 bg-white hover:border-purple-300 hover:bg-purple-50'
                  } ${showResult ? 'cursor-default' : 'cursor-pointer'}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                        selectedAnswer === index
                          ? showResult
                            ? isCorrect
                              ? 'bg-green-600 text-white'
                              : 'bg-red-600 text-white'
                            : 'bg-purple-700 text-white'
                          : showResult && index === currentQuestion.correctAnswer
                          ? 'bg-green-600 text-white'
                          : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {String.fromCharCode(65 + index)}
                    </div>
                    <span className="text-slate-800 text-base leading-relaxed pt-1">{option}</span>
                    {showResult && selectedAnswer === index && (
                      isCorrect ? (
                        <CheckCircle2 className="w-6 h-6 text-green-600 ml-auto flex-shrink-0" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600 ml-auto flex-shrink-0" />
                      )
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Explanation */}
        {showResult && (
          <Alert className={`mb-6 border-2 ${isCorrect ? 'border-green-400 bg-green-50' : 'border-orange-400 bg-orange-50'}`}>
            <AlertDescription>
              <p className="font-semibold text-slate-900 mb-2">
                {isCorrect ? '✓ Correct!' : '✗ Incorrect'}
              </p>
              <p className="text-slate-700 leading-relaxed">{currentQuestion.explanation}</p>
            </AlertDescription>
          </Alert>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4">
          {!showResult ? (
            <Button
              onClick={handleSubmit}
              disabled={selectedAnswer === null || attempts >= 4}
              className="flex-1 bg-purple-700 hover:bg-purple-800 text-white py-6 text-lg transition-colors"
            >
              Submit Answer
            </Button>
          ) : (
            <>
              {isCorrect ? (
                <Button
                  onClick={handleNext}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6 text-lg transition-colors"
                >
                  {currentQuestionIndex < totalQuestions - 1 ? 'Next Question' : 'Complete Challenge'}
                </Button>
              ) : (
                <Button
                  onClick={handleRetry}
                  disabled={attempts >= 4}
                  className="flex-1 bg-purple-700 hover:bg-purple-800 text-white py-6 text-lg transition-colors"
                >
                  {attempts >= 4 ? 'Study Required' : 'Try Again'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChallengeInterface;